import { Action, IOWidget, mk_pair, mk_widget, Widget } from 'widgets-for-react'
import React from 'react'
import { CMSState, Link } from '../types.gen'
import { link, getClassName } from '../utils.gen'
import { CookieSensitiveContentWrapper } from '../cookiebar.mod'
import Plyr from 'react-plyr'
import { Link as RouterLink } from 'react-router-dom'
import { mediaNestedBlockClasses, textBlockClasses } from '../../../../components/public_site/blocks/style.mod'
import { acceptCookies } from '../cookiebar.utils.mod'


export type ExpandBlockProps = {
  block_index: number
  title?: string
  intro?: string
  items: ExpandProps[]
  link?: Link

  show_in_page_navigation: boolean
  navigation_title: string
}

export type ExpandItemBlocks = {
  blockKind: string,
  innerText: string
  props?: {
    [name: string]: any
  }
}

export type ExpandProps = {
  id?: string;
  title: string;
  blocks: ExpandItemBlocks[]
  open: boolean;
}

export type ExpandBlockStyle = Partial<{
  block_classname: string
  inner_classname: string
  header_classname: string
  title_classname: string
  intro_classname: string
  content_classname: string
  text_classname: string
  footer_classname: string
  clickable_classname: string
  list_classname: string
  expand_item_classname: string
  expand_button_classname: string
  expand_title_classname: string
  expand_icon_classname: string
  expand_text_classname: string
}>

export function ExpandBlock<CustomBlockData, extra_images>(
  props: ExpandBlockProps,
  options?: { style?: ExpandBlockStyle; key?: string }
): IOWidget<CMSState<CustomBlockData, extra_images, never>, Action<CMSState<CustomBlockData, extra_images, never>>> {
  return (s: CMSState<CustomBlockData, extra_images, never>) =>
    mk_widget({
      run: onDone => (
        <section
          id={props.navigation_title}
          className={getClassName(options, 'block_classname', 'container')}
          key={options && options.key ? options.key : 'expand-block'}
        >
          <div className={getClassName(options, 'inner_classname', 'py-5')}>
            {(props.title || props.intro) && (
              <header className={getClassName(options, 'header_classname', '')}>
                {props.title && <h2 className={getClassName(options, 'title_classname', 'mb-3')}>{props.title}</h2>}
                {props.intro && <div className={getClassName(options, 'intro_classname', '')} dangerouslySetInnerHTML={{ __html: props.intro }}></div>}
              </header>
            )}

            <div className={getClassName(options, 'content_classname', '')}>
              <ul className={getClassName(options, 'list_classname', 'accordion list-unstyled mb-3')}>
                {props.items.map((value, index) =>
                  Expand(
                    {
                      id: value.title + `${index}`,
                      blocks: value.blocks,
                      open: value.open,
                      title: value.title
                    },
                    options
                  ).run(action => {
                    let openNew = action(value.open)
                    onDone(s1 => {
                      let currentBlock = s1.blocks.get(props.block_index) as any
                      if (currentBlock.kind === 'expandBlock') {
                        currentBlock = {
                          ...currentBlock,
                          value: mk_pair(
                            {
                              ...currentBlock.value.fst,
                              items: currentBlock.value.fst.items.map((e: any, i: number) =>
                                i === index ? { ...e, open: openNew } : e
                              )
                            },
                            currentBlock.value.snd
                          )
                        }
                      }
                      return { ...s1, blocks: s1.blocks.set(props.block_index, currentBlock) }
                    })
                  })
                )}
              </ul>
            </div>

            {props.link && (
              <footer className={getClassName(options, 'footer_classname', '')}>
                {link(
                  props.link,
                  { classname: getClassName(options, 'clickable_classname', 'btn btn-link pl-0') },
                  props.link.title
                )}
              </footer>
            )}
          </div>
        </section>
      )
    })
}

export function Expand(props: ExpandProps, options?: { style?: ExpandBlockStyle; key?: string }): Widget<Action<boolean>> {
  return mk_widget({
    run: onDone => (
      <li className={getClassName(options, 'expand_item_classname', 'card')} key={props.id}>
        <button
          onClick={() => onDone(s => !s)}
          className={getClassName(
            options,
            'expand_button_classname',
            `card-header ${props.open ? 'col--open' : 'col--closed'} text-left border-0`
          )}
          aria-controls={`a11y-${props.id}`}
          aria-expanded={props.open}
        >
          <h3 className={getClassName(options, 'expand_title_classname', 'btn btn-link m-0 pl-0')}>{props.title}</h3>
          <span
            className={getClassName(options, 'expand_icon_classname', 'font-weight-bold mr-2 text-primary')}
            aria-hidden="true"
          >
            {props.open ? '-' : '+'}
          </span>
        </button>

        <div
          id={`a11y-${props.id}`}
          className={getClassName(
            options,
            'expand_text_classname',
            `card-body pb-1 ${props.open ? '' : 'd-none'} text-left border-0`
          )}
          aria-hidden={!props.open}
        // dangerouslySetInnerHTML={{ __html: props.text }}
        >
          {props.blocks.map((b, i) => {
            if (b.blockKind == 'core/embed') {
              let media_options = { style: mediaNestedBlockClasses }
              return <section
                className={getClassName(media_options, 'block_classname', 'container')}
                key={media_options && options.key ? options.key : 'media-block'}
              >
                <figure className={getClassName(media_options, 'figure_classname', '')}>
                  <div className={getClassName(media_options, 'media_classname', 'text-center')}>
                    <CookieSensitiveContentWrapper
                      options={{
                        style:
                          media_options && media_options.style
                            ? {
                              cookies_classname: media_options.style.cookies_classname,
                              cookies_inner_classname: media_options.style.cookies_inner_classname,
                              cookies_text_classname: media_options.style.cookies_text_classname,
                              cookies_clickable_classname: media_options.style.cookies_clickable_classname
                            }
                            : undefined
                      }}
                      placeholder={
                        <>
                          Wijzig je cookievoorkeuren om deze video te bekijken in Compleet of Beperkt.{' '}
                          <RouterLink to="/cookies">Meer informatie over cookies</RouterLink>
                          <br />
                          <button className="btn btn--primary" onClick={() => acceptCookies('complete')}>Accepteer cookies</button>
                        </>
                      }
                    >
                      {b.props && b.props.url && b.props.providerNameSlug ?
                        <Plyr type={b.props.providerNameSlug} videoId={b.props.url} />
                        : null
                      }
                    </CookieSensitiveContentWrapper>
                  </div>


                  <figcaption className={getClassName(media_options, 'figcaption_classname', '')}>
                    <p
                      dangerouslySetInnerHTML={{ __html: b.innerText.replace(b.props && b.props.url ? b.props.url : '', '') }}
                      className={getClassName(media_options, 'caption_classname', 'small text-secondary text-center mt-2')}
                    />
                  </figcaption>

                </figure>
              </section>
            }
            let text_options = { style: textBlockClasses }
            return <section
              className={getClassName(text_options, 'block_classname', 'container')}
              key={text_options && options.key ? options.key : 'text-block'}
            >

              <div className={getClassName(text_options, 'content_classname', '')}>
                <div
                  className={getClassName(text_options, 'text_classname', '')}
                  dangerouslySetInnerHTML={{ __html: b.innerText }}
                />
              </div>
            </section>
          })}
        </div>
      </li>
    )
  })
}



// WEBPACK FOOTER //
// ./Client/components/cms/blocks/widgets/expandBlock.gen.tsx